// Product lines of the STO details modal. One line per vendor item of `sto.items[]`, in vendor order, every line read
// from its OWN item. Formatting is display-only: the response is never mutated, no tax is recalculated, nothing is
// defaulted. A missing / null / blank value is an em dash, and so is any unit price, quantity or VAT that is not a plain
// finite decimal ("abc", "1,5", "NaN", Infinity): never 0, NaN or an invented value.
export const MISSING_VALUE = '—'

export const hasRowValue = (value: any): boolean =>
    value !== null && value !== undefined && !(typeof value === 'string' && value.trim() === '')

export interface ProductLine {
    name: string
    unitPrice: string
    quantity: string
    currency: string
    vat: string
}

export const PRODUCT_COLUMNS: { key: 'name' | 'unitPrice' | 'quantity' | 'currency' | 'vat'; label: string }[] = [
    { key: 'name', label: 'שם הפריט' },
    { key: 'unitPrice', label: 'מחיר ליחידה' },
    { key: 'quantity', label: 'כמות' },
    { key: 'currency', label: 'מטבע' },
    { key: 'vat', label: 'מע״מ' }
]

const DECIMAL = /^([+-]?)(\d*)(?:\.(\d*))?$/

// The supplied value as plain decimal parts, or null when it is not a plain decimal number.
const decimalParts = (value: any): { negative: boolean; int: string; frac: string } | null => {
    let text: string
    if (typeof value === 'number') {
        if (!isFinite(value)) return null
        text = String(value)
        if (/e/i.test(text)) text = value.toFixed(20).replace(/0+$/, '')
    } else if (typeof value === 'string') {
        text = value.trim()
    } else {
        return null
    }
    const match = DECIMAL.exec(text)
    if (!match || (match[2] === '' && (match[3] === undefined || match[3] === ''))) return null
    return { negative: match[1] === '-', int: match[2] || '0', frac: match[3] || '' }
}

const stripLeadingZeros = (digits: string): string => digits.replace(/^0+(?=\d)/, '')

const shown = (value: any): string => String(typeof value === 'string' ? value.trim() : value)

// Exactly two decimals, rounded half away from zero on the decimal digits (no binary floating-point error).
export const formatUnitPrice = (value: any): string => {
    if (!hasRowValue(value)) return MISSING_VALUE
    const parts = decimalParts(value)
    if (!parts) return MISSING_VALUE
    const frac = (parts.frac + '00').slice(0, 2)
    const digits = stripLeadingZeros(parts.int + frac).split('')
    if (parts.frac.length > 2 && parts.frac.charAt(2) >= '5') {
        let at = digits.length - 1
        while (at >= 0) {
            if (digits[at] === '9') {
                digits[at] = '0'
                at -= 1
            } else {
                digits[at] = String(Number(digits[at]) + 1)
                break
            }
        }
        if (at < 0) digits.unshift('1')
    }
    let text = digits.join('')
    while (text.length < 3) text = '0' + text
    const out = text.slice(0, -2) + '.' + text.slice(-2)
    return parts.negative && /[1-9]/.test(out) ? '-' + out : out
}

// Insignificant trailing zeros removed, fractional quantities preserved (1.00 -> 1, 1.50 -> 1.5, 0.250 -> 0.25).
export const formatQuantity = (value: any): string => {
    if (!hasRowValue(value)) return MISSING_VALUE
    const parts = decimalParts(value)
    if (!parts) return MISSING_VALUE
    const frac = parts.frac.replace(/0+$/, '')
    const int = stripLeadingZeros(parts.int)
    const out = frac ? int + '.' + frac : int
    return parts.negative && /[1-9]/.test(out) ? '-' + out : out
}

// The supplied percentage with a percent sign (0 -> 0%, valid decimals as supplied); anything invalid is a dash.
export const formatVatPercent = (value: any): string => {
    if (!hasRowValue(value)) return MISSING_VALUE
    return decimalParts(value) ? shown(value) + '%' : MISSING_VALUE
}

export const formatCurrency = (value: any): string => (hasRowValue(value) ? shown(value) : MISSING_VALUE)

export const formatItemName = (value: any): string => (hasRowValue(value) ? shown(value) : MISSING_VALUE)

const isObject = (value: any): boolean => value !== null && typeof value === 'object' && !Array.isArray(value)

const lineOf = (source: any, name: any): ProductLine => {
    const item = isObject(source) ? source : {}
    return {
        name: formatItemName(name),
        unitPrice: formatUnitPrice(item.unit_price),
        quantity: formatQuantity(item.units_number),
        currency: formatCurrency(item.price_currency),
        vat: formatVatPercent(item.vat_percent)
    }
}

// Non-empty items[]: every entry once, in order, each from its own fields. Otherwise (items missing, null or exactly
// []): the single legacy line from `sto.item` (its name, including a PDF-derived one, and its legacy fields when the
// payload carries them); what is not available is a dash.
export const productLines = (sto: any): ProductLine[] => {
    const source = sto || {}
    if (Array.isArray(source.items) && source.items.length > 0) {
        return source.items.map((entry: any) => lineOf(entry, isObject(entry) ? entry.name : undefined))
    }
    const legacy = isObject(source.item) ? source.item : {}
    return [lineOf(legacy, legacy.name)]
}
